import * as React from 'react'

import { SocialShare } from '@thg-commerce/gravity-elements'
import { SocialShareIcon } from '@thg-commerce/gravity-elements/SocialShare/SocialShare'

import { useSiteDefinition, useRequestConfig } from '../ConfigurationLoader'
import { i18n } from '../i18n'

import {
  StyledTwitterIcon,
  StyledFacebookIcon,
  StyledEmailIcon,
  StyledWhatsAppIcon,
  StyledPinterestIcon,
} from './style'

export type Network =
  | 'twitter'
  | 'facebook'
  | 'pinterest'
  | 'email'
  | 'whatsapp'

export interface SocialShareRendererProps {
  networks: Network[]
  url: string
  title: string
  text: string
  className?: string
}

interface ShareContent {
  url: string
  title: string
  text: string
}

interface NetworkDefinition {
  url: UrlBuilder
  mobileOnly?: boolean
  enabled?: boolean
}

type UrlBuilder = (share: ShareContent) => string
type NetworkMap = {
  [network in Network]: { [country: string]: NetworkDefinition }
}

const networkMap: NetworkMap = {
  twitter: {
    default: {
      url: ({ url, text }) =>
        `https://www.twitter.com/share?url=${url}&text=${encodeURIComponent(
          text,
        )}`,
    },
  },
  facebook: {
    default: {
      url: ({ url }) => `https://www.facebook.com/share.php?u=${url}`,
    },
  },
  pinterest: {
    default: {
      url: ({ url }) =>
        `https://www.pinterest.com/pin/create/button/?url=${url}`,
    },
  },
  email: {
    default: {
      url: ({ url, title }) =>
        `mailto:?subject=${encodeURIComponent(title)}&body=${url}`,
    },
  },
  whatsapp: {
    default: {
      url: ({ url }) => `whatsapp://send?text=${url}`,
    },
  },
}

type IconMap = {
  [network in Network]: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
}
const iconMap: IconMap = {
  twitter: StyledTwitterIcon,
  facebook: StyledFacebookIcon,
  email: StyledEmailIcon,
  whatsapp: StyledWhatsAppIcon,
  pinterest: StyledPinterestIcon,
}

export const getNetworks = (networks: string[]): Network[] => {
  const availableNetworks: Network[] = [
    'twitter',
    'facebook',
    'email',
    'whatsapp',
    'pinterest',
  ]

  return availableNetworks.filter((availableNetwork) => {
    return networks.includes(availableNetwork)
  })
}

const getNetworkDefinition = (network: Network, locale: string) => {
  const networkCountryMap = networkMap[network]
  return networkCountryMap[locale] || networkCountryMap['default']
}

export const SocialShareRenderer = ({
  networks,
  url,
  title,
  text,
  className,
}: SocialShareRendererProps) => {
  const { defaultLocale } = useSiteDefinition()
  const { userAgent } = useRequestConfig()
  const orderedNetworks = Object.keys(networkMap)
  const configuredNetworks = networks
    .sort((a, b) => orderedNetworks.indexOf(a) - orderedNetworks.indexOf(b))
    .reduce((filtered, network) => {
      const { mobileOnly, enabled, url: urlBuilder } = getNetworkDefinition(
        network,
        defaultLocale,
      )
      if (enabled === false) {
        return filtered
      }
      filtered.push({
        title: i18n(`general.social_share.${network}.text`),
        href: urlBuilder({ url, title, text }),
        icon: iconMap[network],
        mobileOnly: mobileOnly || false,
      })
      return filtered
    }, [] as SocialShareIcon[])
  return (
    <SocialShare
      icons={configuredNetworks}
      userAgent={userAgent}
      className={className}
    />
  )
}
